import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/contact", form);
      toast.success(r.data?.message || "Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="contact-page">
      <Header />

      <section className="px-6 sm:px-12 lg:px-20 pt-16 pb-24 max-w-3xl mx-auto">
        <p className="label-eyebrow mb-3 inline-flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-[#C05C42]" /> Contact
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2D322B]" data-testid="contact-title">
          Get in touch.
        </h1>

        <div
          className="mt-8 flex items-start gap-4 bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-5 sm:p-6"
          data-testid="contact-join-notice"
        >
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#D49A44]/20 flex items-center justify-center">
            <Clock className="w-4.5 h-4.5 text-[#D49A44]" />
          </div>
          <p className="text-base text-[#5C6359] leading-relaxed">
            If you are wanting to join our program, please contact us through the form below and we will
            be in touch to <strong className="text-[#2D322B]">assess your application within 7 business days</strong>.
          </p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="contact-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Your name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-name"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2.5 outline-none text-[#2D322B] text-lg"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2.5 outline-none text-[#2D322B] text-lg"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Message</label>
            <textarea
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message"
              className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-4 outline-none text-[#2D322B]"
              placeholder="Tell us a little about yourself and why you'd like to join…"
            />
          </div>
          <div className="pt-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </section>

      <Footer />
    </div>
  );
}
